/*
 * `theTime` and `theUTCTime` factory functions
 */

import type { TimeDateComponent, TimeComponent } from "./types";
import { setNewDate, dateGet, type Settings, type SettingsZ } from "./functions";

type DateInit = Date | TimeDate | Settings | string;
type Factory = (init?: Date | TimeDate | Settings | string, whereOptions?: Settings) => TimeDate;

/**
 * new Date object of extended TimeDate object.
 *
 * - Defaults to current time.
 * - If `t` is not set (excluding `add` time), local time will be used,
 *   else if `t` is set, even empty string, unset time components will default to 0.
 * - Option `MM` to set Date start from 1, not native 0, others are intuitive as original.
 *
 * @param init: string | Date | TimeDate | Settings – Date object, date time string, or set options.
 * @param whereOptions: Settings -- set options set init when init isn't options.
 * @returns TimeDate – extended Date object. */
const theTime: Factory = (init, whereOptions) => {
	const settings = findSettings(init, whereOptions);

	return (settings.Z
		? theUTCTime(init, whereOptions) // TODO: rewrite to not use theUTCTime, but only one-off UTC
		: setNewDate(settings, newTimeDate(theTime, init), false)
	) as TimeDate;
};

// Like Time, but with UTC time
const theUTCTime: Factory = (init, whereOptions) => {
	return setNewDate(
		{ Z: true, ...findSettings(init, whereOptions), },
		newUTCTimeDate(theUTCTime, init),
		false
	) as TimeDate;
}

class TimeDate extends Date {
	private _new: Factory;
	private _Z?: boolean;

	constructor(opt: SettingsZ & { next: Factory, init?: string | Date | TimeDate; }) {
		if (opt.init) super(opt.init);
		else super();
		this._new = opt.next;
		this._Z = opt.Z || false;
	}
	// Set
	where(c: Settings | TimeDateComponent = {}, value?: string | number) {
		return this._new(this, typeof c === "string" ? { [c]: value } : c);
	}
	// Set as addition/subtraction
	goes(c: Settings | TimeDateComponent = {}, value?: string | number) {
		return this._new(this, {
			...(typeof c === "string" ? { [c]: value } : c), add: true
		});
	}
	// Set by dayOfWeek @returns TimeDate object in this' week
	whereDayOfWeek(weekday: 1 | 2 | 3 | 4 | 5 | 6 | 7) {
		return this._new(this).goes({ DD: weekday - (this.getDay() || 7) });
	}
	// Set to last day of month
	whereLastDayOfMonth() {
		return this._new(this, { DD: 1 }).goes({ MM: +1 }).where({ DD: 0 });
	}
	// Get time component string
	get(c?: TimeComponent, options?: SettingsZ): string {
		return dateGet({
			...(c ? { component: c } : { component: "epoch" }),
			...(options?.Z || this._Z ? { UTC: options?.Z || this._Z } : {})
		}, this);
	}
	// Get time components string[]
	list(c: TimeComponent | TimeComponent[], opt?: SettingsZ): string[] {
		return (Array.isArray(c) ? c : [c])
			.reduce((list, c) => [...list, this.get(c, opt)], [] as string[]);
	}
	// Get joined time components string
	to(c: TimeComponent | TimeComponent[], y?: string | SettingsZ, z?: SettingsZ): string {
		return this.list(c, z ? z : typeof y === "object" ? y : {}).join(typeof y === "string" ? y : "");
	}
	// Get plain date string
	toPlainDateString(as: SettingsZ = {}): string {
		return this.to(["YYYY", "MM", "DD"], "-", as);
	}
}

// @returns TimeDate object from an ISO date string
const newTimeFromDateTimeString = (dT: string, factory: Factory, Z = false): TimeDate => {
	// If is date time string with timezone, it can be used to create a Date object
	if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[-+]\d{2}:\d{2})$/.test(dT))
		return new TimeDate({ init: dT, next: factory, Z });

	const [y, m, d] = dT.split("-");
	const timeString = dT.split("T")[1];
	const as = (org = "1", to: number) => org.padStart(to, "0").substring(0, to);

	return factory({ YYYY: as(y, 4), MM: as(m, 2), DD: as(d, 2), T: timeString || 0, Z }) as TimeDate;
};

// @returns object from init, string, or a new TimeDate object
const newTimeDate = (factory: Factory, init?: DateInit): TimeDate =>
	init instanceof Date || init instanceof TimeDate
		? new TimeDate({ init, next: factory }) : typeof init === "string"
			? newTimeFromDateTimeString(init, factory)
			: new TimeDate({ next: factory });

const newUTCTimeDate = (factory: Factory, init?: DateInit): TimeDate =>
	init instanceof Date || init instanceof TimeDate
		? new TimeDate({ init, Z: true, next: factory }) : typeof init === "string"
			? newTimeFromDateTimeString(init, factory, true)
			: new TimeDate({ Z: true, next: factory });

const findSettings = (
	init?: DateInit,
	addOptions?: Settings
): Settings =>
	init instanceof Object && !(init instanceof Date) && !(init instanceof TimeDate)
		? init : addOptions || {};

export default theTime;
export { theTime, theUTCTime, type TimeDate};

